import CustomButtonNew from 'Common/CustomButtonNew/CustomButtonNew'
import React, { useCallback, useRef, useState } from 'react'

const DragAndDropUpload = ({
    id,
    accept = '.pdf,.jpg,.jpeg,.png,.gif,.xlsx,.xls',
    disabled = false,
    multiple = false,
    name,
    onFilesSelected,
    className = '',
}) => {
    const inputRef = useRef(null)
    const [isDragging, setIsDragging] = useState(false)

    const resetInputValue = useCallback(() => {
        if (inputRef.current) {
            inputRef.current.value = null
        }
    }, [])

    const emitFileChange = useCallback(
        (files) => {
            if (!files?.length || !onFilesSelected) {
                return
            }

            const syntheticEvent = {
                target: {
                    files,
                    name,
                },
            }

            onFilesSelected(syntheticEvent)
        },
        [name, onFilesSelected],
    )

    const handleInputChange = useCallback(
        (event) => {
            if (disabled) {
                return
            }

            onFilesSelected?.(event)
            resetInputValue()
            setIsDragging(false)
        },
        [disabled, onFilesSelected, resetInputValue],
    )

    const handleBrowseClick = useCallback(() => {
        if (disabled) {
            return
        }

        inputRef.current?.click()
    }, [disabled])

    const handleDragOver = useCallback(
        (event) => {
            event.preventDefault()
            if (disabled) {
                return
            }
            if (!isDragging) {
                setIsDragging(true)
            }
        },
        [disabled, isDragging],
    )

    const handleDragLeave = useCallback(() => {
        if (isDragging) {
            setIsDragging(false)
        }
    }, [isDragging])

    const handleDrop = useCallback(
        (event) => {
            event.preventDefault()
            event.stopPropagation()
            if (disabled) {
                return
            }

            setIsDragging(false)
            const droppedFiles = event.dataTransfer?.files
            if (!droppedFiles?.length) {
                return
            }

            emitFileChange(droppedFiles)
            resetInputValue()
        },
        [disabled, emitFileChange, resetInputValue],
    )

    const handleKeyDown = useCallback(
        (event) => {
            if (disabled) {
                return
            }

            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                handleBrowseClick()
            }
        },
        [disabled, handleBrowseClick],
    )

    return (
        <div
            className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg transition-shadow duration-150 ease-in-out p-3 text-center bg-white ${disabled ? 'border-secondaryLight bg-[#F5F5F5] cursor-not-allowed opacity-60' : 'cursor-pointer border-[#D0D5DD] hover:shadow-md'} ${isDragging ? 'border-primary bg-primaryLightColor/40' : ''} ${className}`}
            onClick={handleBrowseClick}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onKeyDown={handleKeyDown}
            role="button"
            tabIndex={disabled ? -1 : 0}
        >
            <img src="/img/addAttachment.svg" alt="upload" className="w-5 h-5 mb-3" />
            <p className="mb-1 text-base font-semibold text-primary">
                Drag & drop files here
            </p>
            <p className="mb-3 text-sm text-secondaryLight">
                or click to browse from your computer
            </p>
            < CustomButtonNew label="Browse Files"
                onClick={(event) => {
                    event.stopPropagation()
                    handleBrowseClick()
                }}
                disabled={disabled}
            />
            <input
                ref={inputRef}
                type="file"
                id={id}
                name={name}
                accept={accept}
                multiple={multiple}
                onChange={handleInputChange}
                disabled={disabled}
                className="hidden"
            />
        </div>
    )
}

export default DragAndDropUpload

